import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CardContent,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AppointmentCalendar from 'pages/Dashboard/AppointmentCalendar';
import { useMutation, useQuery, UseQueryResult } from 'react-query';
import {
  HrRefreshCreatePayload,
  HrRefreshHistory,
  HrRefreshHistoryStatus,
} from 'pages/Dashboard/types/healthRecords.types';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { formatTimeDifference, GoogleDate } from 'utils/dateUtils';
import { useHttp } from 'hooks/use-fetch';
import HLDrawer from 'components/HLDrawer';
import HLButton from 'components/HLButton';
import { yellow } from '@mui/material/colors';
import MedicalHistoryDrawer from 'pages/Dashboard/MedicalHistoryDrawer';
import ExpandMoreIcon from '@mui/icons-material/ArrowDropDown';
import HLCard from 'components/HLCard';
import { fetchPatientHealthRecordStatus } from 'pages/Dashboard/services/patient.services';
import { healthRecordRefreshUrls, patientUrls } from 'utils/apiUrls';
import { PatientRecordRetrievalStatus } from 'pages/Dashboard/types';
import { createHrRefresh, healthRecordRefreshHistory } from 'pages/Dashboard/services/healthrecords.services';
import { ToastType } from 'components/Common/Toast';
import ContentLoader from 'components/Common/ContentLoader';
import HealthRecordRefresh from 'pages/Dashboard/HealthRecordRefresh';
import { PatientStatusResponse } from 'pages/Dashboard/types/patient.types';

function WorkInProgressIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='105' height='105' viewBox='0 0 105 105' fill='none'>
      <path d='M55.7812 21.3281C55.7812 19.516 54.3122 18.0469 52.5 18.0469C50.6878 18.0469 49.2188 19.516 49.2188 21.3281V36.0938C49.2188 37.0906 49.6719 38.0333 50.4502 38.6561L58.6533 45.2186C60.0685 46.3506 62.1334 46.1213 63.2654 44.706C64.3975 43.2908 64.1681 41.226 62.7529 40.0939L55.7812 34.5168V21.3281Z' fill='#E5772C' />
      <path fillRule='evenodd' clipRule='evenodd' d='M22.9688 36.0938C22.9688 19.7841 36.1902 6.5625 52.5 6.5625C68.8098 6.5625 82.0312 19.7841 82.0312 36.0938C82.0312 52.4035 68.8098 65.625 52.5 65.625C36.1902 65.625 22.9688 52.4035 22.9688 36.0938ZM52.5 13.125C39.8147 13.125 29.5312 23.4085 29.5312 36.0938C29.5312 48.7791 39.8147 59.0625 52.5 59.0625C65.1853 59.0625 75.4688 48.7791 75.4688 36.0938C75.4688 23.4085 65.1853 13.125 52.5 13.125Z' fill='#E5772C' />
      <path d='M19.6875 82.0312C17.8753 82.0312 16.4062 83.5003 16.4062 85.3125C16.4062 87.1247 17.8753 88.5938 19.6875 88.5938H39.375C41.1872 88.5938 42.6562 87.1247 42.6562 85.3125C42.6562 83.5003 41.1872 82.0312 39.375 82.0312H19.6875Z' fill='#E5772C' />
      <path fillRule='evenodd' clipRule='evenodd' d='M19.6875 72.1875C12.4388 72.1875 6.5625 78.0639 6.5625 85.3125C6.5625 92.5611 12.4388 98.4375 19.6875 98.4375H85.3125C92.5611 98.4375 98.4375 92.5611 98.4375 85.3125C98.4375 78.0639 92.5611 72.1875 85.3125 72.1875H19.6875ZM13.125 85.3125C13.125 81.688 16.0631 78.75 19.6875 78.75H85.3125C88.937 78.75 91.875 81.688 91.875 85.3125C91.875 88.937 88.937 91.875 85.3125 91.875H19.6875C16.0631 91.875 13.125 88.937 13.125 85.3125Z' fill='#E5772C' />
    </svg>
  );
}

interface RecordRefreshDrawerProps {
  patientId: string | undefined;
  isOpen: boolean;
  // isDemoPatient?: boolean;
  setShowRecordRefreshDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedChip?: React.Dispatch<React.SetStateAction<boolean>>;
  hrRefreshHistory?: UseQueryResult<HrRefreshHistory, unknown>;
  hrRecordStatus?: UseQueryResult<PatientStatusResponse, unknown>;
  isPendingApproval?: boolean;
  isFetchingHR?: boolean;
  drawerMode?: boolean;
}

function RecordRefreshDrawer({
  patientId: id,
  isOpen,
  // isDemoPatient,
  setShowRecordRefreshDrawer,
  setSelectedChip,
  hrRefreshHistory,
  hrRecordStatus,
  isPendingApproval,
  isFetchingHR,
  drawerMode = true,
}: RecordRefreshDrawerProps) {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);

  const {
    providerId,
    providerName,
    selectedProviderName,
    http,
    isHrRefreshEnabled,
    setToastProps,
  } = useHttp();

  const handleDrawerClose = (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown'
      && ((event as React.KeyboardEvent).key === 'Tab'
        || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setShowRecordRefreshDrawer(false);
    setSelectedDate(null);
  };

  const getHrRefreshHistory = useQuery({
    queryKey: [healthRecordRefreshUrls.hrRefreshHistory.queryUrl, providerId, id],
    queryFn: healthRecordRefreshHistory(http.get),
    enabled: isHrRefreshEnabled && !hrRefreshHistory && !!providerId && !!id,
  });

  const refreshHistory = hrRefreshHistory || getHrRefreshHistory;

  const healthRecordStatus = useQuery(
    [`${patientUrls.patientHealthRecordStatus.queryUrl}`, providerId, id],
    {
      queryFn: fetchPatientHealthRecordStatus(http.get),
      enabled: !!providerId && !!id,
    },
  );

  const recordStatus = hrRecordStatus || healthRecordStatus;

  const { isLoading } = recordStatus;
  const refreshStatus = refreshHistory.data?.refreshHistory[0]?.status;

  const onClose = () => {
    setShowRecordRefreshDrawer(false);
    setSelectedDate(null);
  };

  const { mutate: hrRefreshCreation,
    // isLoading: isRecordRefreshLoading
  } = useMutation({
    mutationFn: (
      payload: HrRefreshCreatePayload,
    ) => createHrRefresh(http.post, providerId, id as string, { ...payload }),
    onSettled: () => {
      onClose();
      refreshHistory.refetch();
    },
    onSuccess: () => {
      setToastProps({ message: 'Record refresh scheduled successfully', open: true, type: ToastType.success });
    },
    onError: (e) => {
      const errorMessage = ((e instanceof Error ? e.message : e) || 'You are not authorised to perform this action') as string;
      setToastProps({ message: errorMessage, open: true, type: ToastType.error });
    },
    retry: 0,
  });


  const isRecordStatusNotRetrieved = recordStatus.data?.status?.recordRetrievalStatus
    !== PatientRecordRetrievalStatus.PATIENT_RECORD_RETRIEVAL_STATUS_RETRIEVED;

  React.useEffect(() => {
    if (isRecordStatusNotRetrieved) {
      trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_HEALTH_RECORDS_FETCHING_IN_PROGRESS, {
        'patient id': id ?? '',
        'provider id': providerId ?? '',
        'logged in provider name': providerName ?? '',
        'selected provider name': selectedProviderName ?? '',
      });
    }
  }, [id, isRecordStatusNotRetrieved, providerId, selectedProviderName, providerName]);

  const getHrRefreshHistoryLastDate = refreshHistory.data?.refreshHistory[0]?.scheduledOn;

  const createRecordRefresh = () => {
    if (selectedDate) {
      const googleDate = {
        date: {
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1,
          day: selectedDate.getDate(),
        },
      };
      trackMixpanelEvent(
        MixpanelEventName.PATIENT_DETAILS_CALENDAR_REFRESH_RECORDS_BUTTON_CLICKED,
        {
          'patient id': id ?? '',
          'days since last fetch': `${
            getHrRefreshHistoryLastDate?.day
            && getHrRefreshHistoryLastDate?.month
            && getHrRefreshHistoryLastDate?.year
              ? formatTimeDifference(getHrRefreshHistoryLastDate as GoogleDate)
              : 'Unknown'
          }`,
          'provider id': providerId ?? '',
          'logged in provider name': providerName ?? '',
          'selected provider name': selectedProviderName ?? '',
          location: 'patient_history_tab_refresh_records_drawer',
        },
      );
      setSelectedChip?.(false);
      hrRefreshCreation({ appointment: googleDate });
    }
  };


  const shouldEnableHrRefresh = (
    (
      !isRecordStatusNotRetrieved // Means: record *has* been retrieved
      || (refreshHistory?.data?.refreshHistory?.length ?? 0) > 1
    )
    && isHrRefreshEnabled
  ) || undefined;

  const handleRecordRefreshDrawer = () => {
    setShowRecordRefreshDrawer(true);
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_REFRESH_RECORDS_BUTTON_CLICKED, {
      'patient id': id ?? '',
      'provider id': providerId ?? '',
      'logged in provider name': providerName ?? '',
      'selected provider name': selectedProviderName ?? '',
      location: 'patient_history_tab_refresh_record',
    });
  };

  const refreshRecordButton = (
    <HLButton
      onClick={() => createRecordRefresh()}
      variant='contained'
      size='large'
      disabled={!selectedDate}
      startIcon={<RefreshIcon fontSize='large' />}
      fullWidth
      sx={{ mt: isMobile ? 0 : 1.5 }}
    >
      REFRESH RECORDS
    </HLButton>
  );

  return (
    <>
      {!drawerMode && (
      <HealthRecordRefresh
        handleRecordRefreshDrawer={handleRecordRefreshDrawer}
        isDisabled={isPendingApproval}
        refreshStatus={refreshStatus}
        isFetchingHR={isFetchingHR}
      />
      )}
      <HLDrawer
        open={isOpen}
        onClose={handleDrawerClose}
        title='Health records refresh'
        footerItems={shouldEnableHrRefresh && isMobile ? refreshRecordButton : null}
      >
        <ContentLoader isFetching={isLoading} isError={false}>
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
            flexGrow={1}
            height='100%'
          >
            <Box>
              {(refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS || isFetchingHR) && (
                <HLCard>
                  <CardContent>
                    <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                      <WorkInProgressIcon />
                      <Typography variant='h5' mt={6} mb={3} color='text.primary'>
                        In progress
                      </Typography>
                    </Box>
                    <Alert
                      variant='filled'
                      severity='warning'
                      icon={<NotificationsIcon />}
                      sx={{ bgcolor: yellow[50], '& .MuiAlert-icon': { color: yellow[900] } }}
                    >
                      <Typography variant='body2' color='text.primary'>
                        Refreshes may take up to 36 hours. You will receive a notification when it
                        is ready.
                      </Typography>
                    </Alert>
                  </CardContent>
                </HLCard>
              )}
              {shouldEnableHrRefresh
                && !(refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS || isFetchingHR) && (
                  <Box>
                    <Typography fontWeight={600} color='text.primary' variant='h5'>
                      When is the patient’s next appointment?
                    </Typography>
                    <Typography fontWeight={400} color='text.primary' variant='body2' pt={1}>
                      To request a medical record refresh, the patient’s next appointment is
                      required.
                    </Typography>
                    <AppointmentCalendar
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                    />
                    {selectedDate && (
                      <Box pt={2} display='flex' flexDirection='row'>
                        <Alert
                          variant='filled'
                          severity='warning'
                          icon={<NotificationsIcon />}
                          sx={{ bgcolor: yellow[50], '& .MuiAlert-icon': { color: yellow[900] } }}
                        >
                          <Typography variant='body2' color='text.primary'>
                            Refreshes may take up to 36 hours. You will receive a notification when
                            it is ready.
                          </Typography>
                        </Alert>
                      </Box>
                    )}
                    {!isMobile && refreshRecordButton}
                  </Box>
              )}
              {(refreshHistory.data?.refreshHistory?.length ?? 0) > 0 && (
                <Box mt={2}>
                  <Accordion
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      margin: 0,
                      '& .MuiAccordionSummary-root:before': { display: 'none' },
                      '& .MuiAccordionSummary-root': { backgroundColor: '#FFF', marginTop: 0 },
                      '& .MuiAccordionSummary-root.Mui-expanded': {
                        minHeight: 'auto',
                        backgroundColor: '#ebeff3',
                        color: 'text.primary',
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '12px 0',
                        transition: 'none',
                      },
                      '& .MuiAccordionDetails-root': { padding: 0 },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ backgroundColor: 'transparent' }}
                    >
                      <Typography variant='body1' color='primary.main'>
                        View History
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: 'transparent' }}>
                      <MedicalHistoryDrawer getHrRefreshHistory={refreshHistory} />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </Box>
          </Box>
        </ContentLoader>
      </HLDrawer>
    </>
  );
}

export default RecordRefreshDrawer;
